/**
 * Markdown 描画。タイムラインの本文表示と編集画面のプレビューで**同一のレンダラ**を使う
 * （docs/03-editor.md §6）。
 *
 * サニタイズは省略できない。本文は自分で書いたものだけとはいえ、
 * 貼り付けた外部テキストに `<script>` や `onerror=` が混ざる可能性は常にある。
 */

import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { sanitizeTaskCheckbox } from './task-checkbox';

marked.setOptions({
  // 改行をそのまま <br> にする。日記の書き味に合わせる（GFM の既定挙動）。
  breaks: true,
  gfm: true,
});

/** 相対リンクの許可先。内部ノートリンク（/notes/<id>）と画像（/api/images/<id>）のみ。 */
const INTERNAL_LINK_RE = /^\/(notes|api\/images)\//;

let hookInstalled = false;

function installHooks(): void {
  if (hookInstalled) return;
  hookInstalled = true;

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'input' && !sanitizeTaskCheckbox(node as Element)) {
      node.parentNode?.removeChild(node);
    }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node instanceof HTMLAnchorElement) {
      const href = node.getAttribute('href') ?? '';
      if (/^https?:/i.test(href)) {
        // 外部リンクは新しいタブで開き、opener を渡さない。
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      } else if (!INTERNAL_LINK_RE.test(href) && !href.startsWith('#')) {
        // javascript: や data: を含む、想定外のスキームは無効化する。
        node.removeAttribute('href');
      }
    }
    if (node instanceof HTMLImageElement) {
      const src = node.getAttribute('src') ?? '';
      if (!src.startsWith('/api/images/') && !/^https?:/i.test(src)) {
        node.removeAttribute('src');
      }
      // サムネイル生成は v1 では行わない。lazy load と CSS で代替する
      // （docs/01-requirements.md §5.8）。
      node.setAttribute('loading', 'lazy');
      node.setAttribute('decoding', 'async');
    }
  });
}

/** Markdown を安全な HTML 文字列へ変換する。 */
export function renderMarkdown(source: string): string {
  installHooks();
  const raw = marked.parse(source, { async: false });
  return DOMPurify.sanitize(raw, {
    USE_PROFILES: { html: true },
    // GFMのチェック状態だけを許可する。inputはフックで無効化したcheckboxに限定する。
    FORBID_TAGS: ['form', 'button', 'iframe', 'object', 'embed', 'style'],
    FORBID_ATTR: ['style'],
    ADD_ATTR: ['target', 'rel', 'loading', 'decoding'],
  });
}

/**
 * 折りたたみ表示用に本文の先頭だけを描画する。
 * 行数で切るのは、Markdown の途中で切ると記法が壊れるため。
 */
export function renderMarkdownExcerpt(
  source: string,
  maxLines: number,
): { html: string; truncated: boolean } {
  const lines = source.split('\n');
  if (lines.length <= maxLines) {
    return { html: renderMarkdown(source), truncated: false };
  }
  return { html: renderMarkdown(lines.slice(0, maxLines).join('\n')), truncated: true };
}
